/**
 * ONLY · Vistos recientemente (sections/on-recently-viewed.liquid).
 * - Lleva su propio historial (localStorage «onViewedProducts», hasta 13 ids): el de Horizon
 *   («viewedProducts», recently-viewed-products.js) guarda sólo 4 y el Figma pide un carril de
 *   6 tarjetas que desborda. La primera vez arranca con lo que ya guardó Horizon.
 * - Suma el producto que se mira y muestra los anteriores (hasta data-limit, 6 por defecto):
 *   pide la misma sección renderizada en la búsqueda
 *   (/search?q=id:1 OR id:2&section_id=on-recently-viewed). Sin productos queda oculta.
 * - Flechas: corren la lista un ancho visible; se deshabilitan en los extremos.
 * - A mano: con el dedo o el trackpad la lista se desliza sola (overflow-x); con el mouse se arrastra
 *   (Manuela, Asana 1219792927214853: «scrollear el carrusel horizontal manualmente y no solo con
 *   las flechas»). El clic que termina un arrastre no abre el producto.
 */

const STORAGE_KEY = 'onViewedProducts';
const HORIZON_KEY = 'viewedProducts';
const MAX_STORED = 13;

/** @param {string} key */
function readIds(key) {
  try {
    const ids = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(ids) ? ids.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

/**
 * Pone el producto primero en el historial y lo devuelve.
 * @param {string} productId
 */
function remember(productId) {
  let ids = readIds(STORAGE_KEY);
  if (!ids.length) ids = readIds(HORIZON_KEY);
  ids = [productId, ...ids.filter((id) => id !== productId)].slice(0, MAX_STORED);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Sin localStorage (modo privado estricto): se muestra lo que haya.
  }
  return ids;
}

class OnRecentlyViewed extends HTMLElement {
  /** @type {{ id: number, x: number, left: number, moved: boolean } | null} */
  drag = null;

  connectedCallback() {
    this.list = this.querySelector('[data-on-rv-list]');
    this.addEventListener('click', this.onClick);
    this.list?.addEventListener('scroll', this.updateArrows, { passive: true });
    this.list?.addEventListener('pointerdown', this.onPointerDown);
    this.list?.addEventListener('pointermove', this.onPointerMove);
    this.list?.addEventListener('pointerup', this.onPointerUp);
    this.list?.addEventListener('pointercancel', this.onPointerUp);
    this.list?.addEventListener('dragstart', this.onDragStart);
    window.addEventListener('resize', this.updateArrows);
    this.load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.list?.removeEventListener('scroll', this.updateArrows);
    this.list?.removeEventListener('pointerdown', this.onPointerDown);
    this.list?.removeEventListener('pointermove', this.onPointerMove);
    this.list?.removeEventListener('pointerup', this.onPointerUp);
    this.list?.removeEventListener('pointercancel', this.onPointerUp);
    this.list?.removeEventListener('dragstart', this.onDragStart);
    window.removeEventListener('resize', this.updateArrows);
  }

  async load() {
    const current = this.dataset.productId || '';
    const limit = Number(this.dataset.limit) || 6;
    const history = current ? remember(current) : readIds(STORAGE_KEY);
    const ids = history.filter((id) => id !== current).slice(0, limit);
    if (!ids.length || !this.list) return;

    const url = new URL(this.dataset.searchUrl || '/search', window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', this.dataset.sectionFile || 'on-recently-viewed');

    let html = '';
    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;
      html = await response.text();
    } catch {
      return;
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const items = Array.from(doc.querySelectorAll('[data-on-rv-list] > li'));
    if (!items.length) return;

    // En el orden en que se vieron (la búsqueda los devuelve por relevancia).
    const position = (item) => ids.indexOf(item.querySelector('[data-product-id]')?.dataset.productId || '');
    items.sort((a, b) => position(a) - position(b));

    this.list.replaceChildren(...items.map((item) => document.importNode(item, true)));
    this.hidden = false;
    this.updateArrows();
  }

  onClick = (event) => {
    const step = event.target instanceof Element ? event.target.closest('[data-on-rv-step]') : null;
    if (!step || !this.list) return;
    const direction = Number(step.dataset.onRvStep) || 1;
    this.list.scrollBy({ left: direction * this.list.clientWidth, behavior: 'smooth' });
  };

  /** Arrastre con el mouse (el touch y el trackpad ya deslizan la lista). Los botones (talles, AÑADIR) no arrastran. */
  onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this.list) return;
    if (event.target instanceof Element && event.target.closest('button, input, select, textarea, label')) return;
    this.drag = { id: event.pointerId, x: event.clientX, left: this.list.scrollLeft, moved: false };
  };

  onPointerMove = (event) => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id || !this.list) return;
    const dx = event.clientX - drag.x;
    if (!drag.moved) {
      // Hasta 6 px es un clic, no un arrastre.
      if (Math.abs(dx) < 6) return;
      drag.moved = true;
      this.list.setPointerCapture(drag.id);
      this.list.classList.add('is-dragging');
    }
    this.list.scrollLeft = drag.left - dx;
  };

  onPointerUp = (event) => {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    this.drag = null;
    const list = this.list;
    if (!drag.moved || !list) return;

    list.classList.remove('is-dragging');
    if (list.hasPointerCapture(drag.id)) list.releasePointerCapture(drag.id);

    // El mousedown enfocó el link de la tarjeta agarrada: sin esto su compra rápida quedaba a la vista
    // (:focus-within) aunque el mouse ya no estuviera encima.
    const active = document.activeElement;
    if (active instanceof HTMLElement && list.contains(active)) active.blur();

    // El clic que sigue al pointerup (mismo evento del mouse) no navega; si no llega, se suelta igual.
    const block = (/** @type {MouseEvent} */ click) => {
      click.preventDefault();
      click.stopPropagation();
    };
    list.addEventListener('click', block, { capture: true, once: true });
    window.setTimeout(() => list.removeEventListener('click', block, { capture: true }), 0);

    // Sin snap mientras se arrastra: al soltar queda alineada a la tarjeta más cercana.
    const listLeft = list.getBoundingClientRect().left;
    const padding = parseFloat(getComputedStyle(list).scrollPaddingInlineStart) || 0;
    const max = list.scrollWidth - list.clientWidth;
    let target = list.scrollLeft;
    let best = Infinity;
    for (const item of list.children) {
      const left = Math.min(max, Math.max(0, list.scrollLeft + item.getBoundingClientRect().left - listLeft - padding));
      if (Math.abs(left - list.scrollLeft) < best) {
        best = Math.abs(left - list.scrollLeft);
        target = left;
      }
    }
    list.scrollTo({ left: target, behavior: 'smooth' });
  };

  /** Sin el arrastre nativo de links y fotos, que se come el del mouse. */
  onDragStart = (event) => {
    event.preventDefault();
  };

  updateArrows = () => {
    if (!this.list) return;
    const max = this.list.scrollWidth - this.list.clientWidth - 1;
    const [prev, next] = this.querySelectorAll('[data-on-rv-step]');
    if (prev) prev.disabled = this.list.scrollLeft <= 0;
    if (next) next.disabled = this.list.scrollLeft >= max;
    this.toggleAttribute('data-no-scroll', max <= 0);
  };
}

if (!customElements.get('on-recently-viewed')) customElements.define('on-recently-viewed', OnRecentlyViewed);
